<template>
  <div class="px-4 mt-10 pb-16">
    <h1 class="text-2xl font-bold mb-1">
      {{ $t("message.image_generator.title") }}
    </h1>
    <p class="text-sm text-gray-500 mb-6">
      {{ $t("message.image_generator.subtitle") }}
    </p>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Panel de configuración -->
      <div ref="configColumn" class="lg:col-span-1 column-scroll">
        <!-- Producto -->
        <label class="block text-sm font-semibold mb-1">
          {{ $t("message.image_generator.product") }}
        </label>
        <div v-if="!selectedProduct" class="relative mb-4">
          <input
            v-model="productSearch"
            type="text"
            class="regular clear full"
            :placeholder="$t('message.image_generator.product_placeholder')"
            @input="onProductSearchInput"
          />
          <div
            v-if="productResults.length || searchingProducts"
            class="absolute z-10 bg-white border rounded w-full max-h-64 overflow-y-auto shadow"
          >
            <div v-if="searchingProducts" class="p-2 text-xs text-gray-400">
              <i class="mdi mdi-loading mdi-spin mr-1"></i>
              {{ $t("message.image_generator.searching") }}
            </div>
            <button
              v-for="product in productResults"
              :key="product.nkrCode"
              class="flex items-center gap-2 w-full text-left p-2 hover:bg-gray-100 border-b"
              @click="selectProduct(product)"
            >
              <img
                v-if="product.image"
                :src="product.image"
                class="w-8 h-8 object-contain"
              />
              <span class="text-xs">
                <span class="font-semibold">{{ product.nkrCode }}</span>
                <span class="block text-gray-500">{{
                  product.customTitle
                }}</span>
              </span>
            </button>
          </div>
        </div>

        <div v-else class="border rounded p-3 mb-4 bg-gray-50">
          <div class="flex justify-between items-start">
            <div>
              <p class="font-semibold text-sm">{{ selectedProduct.nkrCode }}</p>
              <p class="text-xs text-gray-600">
                {{ selectedProduct.title || selectedProduct.description }}
              </p>
              <p class="text-xs text-gray-400">
                {{ selectedProduct.brand }}
                <span v-if="selectedProduct.oe"
                  >· OE {{ selectedProduct.oe }}</span
                >
              </p>
            </div>
            <button
              class="text-gray-400 hover:text-red-600"
              @click="clearProduct()"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-2">
            {{ $t("message.image_generator.product_context_hint") }}
          </p>

          <!-- Fotos del producto como referencia -->
          <div v-if="productImages.length" class="mt-3">
            <p class="text-xs font-semibold mb-1">
              {{ $t("message.image_generator.product_images") }}
            </p>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="image in productImages"
                :key="image.id"
                class="w-14 h-14 border-2 rounded overflow-hidden"
                :class="
                  isProductImageSelected(image)
                    ? 'border-red-600'
                    : 'border-gray-200'
                "
                @click="toggleProductImage(image)"
              >
                <img
                  :src="image.thumbnail || image.imageRoute"
                  class="w-full h-full object-cover"
                />
              </button>
            </div>
          </div>
        </div>

        <AiPromptTemplates
          :key="templatesVersion"
          :current="{
            prompt: prompt,
            aspectRatio: aspectRatio,
            imageSize: imageSize,
            count: count,
            assetIds: assetIds
          }"
          :applied-id="promptTemplateId"
          @apply="applyTemplate"
        />

        <!-- Prompt -->
        <label class="block text-sm font-semibold mb-1">
          {{ $t("message.image_generator.prompt") }}
        </label>
        <textarea
          v-model="prompt"
          rows="6"
          class="regular clear full mb-4"
          :placeholder="$t('message.image_generator.prompt_placeholder')"
          :disabled="loading"
        ></textarea>

        <div class="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label class="block text-sm font-semibold mb-1">
              {{ $t("message.image_generator.aspect_ratio") }}
            </label>
            <select
              v-model="aspectRatio"
              class="regular clear full"
              :disabled="loading"
            >
              <option v-for="ratio in aspectRatios" :key="ratio" :value="ratio">
                {{ ratio }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">
              {{ $t("message.image_generator.size") }}
            </label>
            <select
              v-model="imageSize"
              class="regular clear full"
              :disabled="loading"
            >
              <option v-for="size in imageSizes" :key="size" :value="size">
                {{ size }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-semibold mb-1">
              {{ $t("message.image_generator.count") }}
            </label>
            <select
              v-model.number="count"
              class="regular clear full"
              :disabled="loading"
            >
              <option v-for="n in 4" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <!-- Iteración en curso -->
        <div
          v-if="iterationFrom"
          class="border border-red-200 bg-red-50 rounded p-2 mb-4 flex items-center gap-2"
        >
          <img
            :src="iterationFrom.url"
            class="w-12 h-12 object-cover rounded"
          />
          <div class="flex-grow text-xs">
            <p class="font-semibold">
              {{ $t("message.image_generator.iterating_on") }}
            </p>
            <p class="text-gray-500">#{{ iterationFrom.id }}</p>
            <p class="text-gray-500">
              {{ $t("message.image_generator.iteration_keeps_prompt") }}
            </p>
          </div>
          <button
            class="text-gray-400 hover:text-red-600"
            @click="iterationFrom = null"
          >
            <i class="mdi mdi-close"></i>
          </button>
        </div>

        <AiStyleAssets
          v-model="assetIds"
          :include-defaults="includeDefaultAssets"
          @update:includeDefaults="includeDefaultAssets = $event"
          class="mb-4"
        />

        <!-- Imágenes de referencia subidas -->
        <label class="block text-sm font-semibold mb-1">
          {{ $t("message.image_generator.reference_images") }}
        </label>
        <div
          class="border-2 border-dashed rounded p-4 text-center text-xs cursor-pointer mb-2"
          :class="dragging ? 'border-red-600 bg-red-50' : 'border-gray-300'"
          @click="$refs.referenceInput.click()"
          @dragover.prevent="dragging = true"
          @dragenter.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <i class="mdi mdi-cloud-upload text-2xl block mb-1 text-gray-400"></i>
          {{ $t("message.image_generator.drop_here") }}
        </div>
        <input
          ref="referenceInput"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          class="hidden"
          @change="onReferenceFiles"
        />
        <div v-if="uploadedReferences.length" class="flex flex-wrap gap-2 mb-4">
          <div
            v-for="(reference, index) in uploadedReferences"
            :key="index"
            class="relative w-16 h-16 border rounded overflow-hidden"
          >
            <img
              :src="`data:${reference.mimeType};base64,${reference.data}`"
              class="w-full h-full object-cover"
            />
            <button
              class="absolute top-0 right-0 bg-black bg-opacity-60 text-white px-1 leading-none"
              @click="removeReference(index)"
            >
              <i class="mdi mdi-close text-xs"></i>
            </button>
          </div>
        </div>

        <!-- Revisión automática: Claude mira el resultado y reintenta solo -->
        <div class="border rounded p-2 mb-4">
          <label class="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" v-model="autoReview" :disabled="loading" />
            {{ $t("message.image_generator.auto_review") }}
          </label>
          <p class="text-xs text-gray-500 mt-1">
            {{ $t("message.image_generator.auto_review_hint") }}
          </p>
          <div v-if="autoReview" class="mt-2">
            <label class="block text-xs font-semibold mb-1">
              {{ $t("message.image_generator.max_attempts") }}
            </label>
            <select
              v-model.number="maxAttempts"
              class="regular clear full"
              :disabled="loading"
            >
              <option v-for="n in 4" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <button
          class="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded disabled:opacity-50"
          :disabled="loading || !prompt.trim()"
          @click="generate()"
        >
          <i v-if="loading" class="mdi mdi-loading mdi-spin mr-1"></i>
          <i v-else class="mdi mdi-image-plus mr-1"></i>
          {{
            loading
              ? $t("message.image_generator.generating")
              : $t("message.image_generator.generate")
          }}
        </button>
      </div>

      <!-- Resultados / historial -->
      <div class="lg:col-span-2 column-scroll">
        <div class="flex gap-4 border-b mb-4">
          <button
            class="pb-2 text-sm font-semibold"
            :class="
              tab === 'results'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-500'
            "
            @click="tab = 'results'"
          >
            {{ $t("message.image_generator.results") }}
          </button>
          <button
            class="pb-2 text-sm font-semibold"
            :class="
              tab === 'history'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-500'
            "
            @click="openHistory()"
          >
            {{ $t("message.image_generator.history") }}
          </button>
          <button
            class="pb-2 text-sm font-semibold"
            :class="
              tab === 'templates'
                ? 'border-b-2 border-red-600 text-red-600'
                : 'text-gray-500'
            "
            @click="tab = 'templates'"
          >
            {{ $t("message.image_generator.prompt_templates") }}
          </button>
        </div>

        <!-- Resultados de la sesión -->
        <template v-if="tab === 'results'">
          <div
            v-if="loading"
            class="border border-dashed rounded p-10 text-center text-gray-400"
          >
            <i class="mdi mdi-loading mdi-spin text-4xl block mb-2"></i>
            {{ $t("message.image_generator.generating") }}
          </div>
          <div
            v-else-if="!results.length"
            class="border border-dashed rounded p-10 text-center text-gray-400"
          >
            <i class="mdi mdi-image-outline text-4xl block mb-2"></i>
            {{ $t("message.image_generator.empty_state") }}
          </div>

          <div
            v-for="attempt in results"
            :key="attempt.generationId"
            class="mb-4"
          >
            <!-- Veredicto de Claude sobre el intento -->
            <div
              v-if="attempt.review"
              class="border rounded p-2 mb-2 text-xs"
              :class="
                attempt.review.approved
                  ? 'border-green-500 bg-green-50'
                  : 'border-yellow-500 bg-yellow-50'
              "
            >
              <p class="font-semibold">
                <i
                  :class="
                    attempt.review.approved
                      ? 'mdi mdi-check-circle text-green-600'
                      : 'mdi mdi-alert-circle text-yellow-600'
                  "
                ></i>
                {{
                  attempt.review.approved
                    ? $t("message.image_generator.review_approved")
                    : $t("message.image_generator.review_rejected")
                }}
                <span v-if="attempt.review.score">
                  · {{ attempt.review.score }}/10
                </span>
                <span class="text-gray-400">
                  · {{ $t("message.image_generator.attempt") }}
                  {{ attempt.attempt }}
                </span>
              </p>
              <p class="text-gray-600 mt-1">{{ attempt.review.summary }}</p>
              <ul
                v-if="attempt.review.issues && attempt.review.issues.length"
                class="list-disc list-inside text-gray-600 mt-1"
              >
                <li v-for="(issue, i) in attempt.review.issues" :key="i">
                  {{ issue }}
                </li>
              </ul>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="image in attempt.images"
                :key="image.id"
                class="border rounded overflow-hidden bg-white"
              >
                <img
                  :src="image.url"
                  class="w-full object-contain bg-gray-50"
                />
                <div class="p-3">
                  <p class="text-xs text-gray-500 mb-2 break-words">
                    {{ attempt.prompt }}
                  </p>
                  <p class="text-xs text-gray-400 mb-2">
                    #{{ image.id }} · {{ image.width }}x{{ image.height }}
                    <span v-if="attempt.nkrCode">· {{ attempt.nkrCode }}</span>
                  </p>
                  <div class="flex flex-wrap gap-2">
                    <button
                      class="text-xs border rounded px-2 py-1 hover:bg-gray-100"
                      @click="iterate(image)"
                    >
                      <i class="mdi mdi-image-edit mr-1"></i>
                      {{ $t("message.image_generator.iterate") }}
                    </button>
                    <a
                      class="text-xs border rounded px-2 py-1 hover:bg-gray-100"
                      :href="image.url"
                      target="_blank"
                    >
                      <i class="mdi mdi-open-in-new mr-1"></i>
                      {{ $t("message.image_generator.open") }}
                    </a>
                    <button
                      class="text-xs border rounded px-2 py-1 hover:bg-gray-100"
                      @click="copyUrl(image.url)"
                    >
                      <i class="mdi mdi-content-copy mr-1"></i>
                      {{ $t("message.image_generator.copy_url") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Historial persistido -->
        <template v-else-if="tab === 'history'">
          <div class="flex items-center gap-3 mb-3 text-xs">
            <label class="flex items-center gap-1">
              <input
                type="checkbox"
                v-model="historyOnlyMine"
                @change="loadHistory()"
              />
              {{ $t("message.image_generator.only_mine") }}
            </label>
            <label v-if="selectedProduct" class="flex items-center gap-1">
              <input
                type="checkbox"
                v-model="historyOnlyProduct"
                @change="loadHistory()"
              />
              {{ selectedProduct.nkrCode }}
            </label>
            <button class="border rounded px-2 py-1" @click="loadHistory()">
              <i class="mdi mdi-refresh"></i>
            </button>
          </div>

          <div
            v-if="loadingHistory"
            class="border border-dashed rounded p-10 text-center text-gray-400"
          >
            <i class="mdi mdi-loading mdi-spin text-4xl block mb-2"></i>
          </div>
          <div
            v-else-if="!generations.length"
            class="border border-dashed rounded p-10 text-center text-gray-400"
          >
            {{ $t("message.image_generator.no_history") }}
          </div>

          <div
            v-for="generation in generations"
            :key="generation.id"
            class="border rounded p-3 mb-3"
          >
            <div class="flex justify-between text-xs text-gray-400 mb-1">
              <span>
                #{{ generation.id }} · {{ generation.userName || "-" }}
                <span v-if="generation.nkrCode"
                  >· {{ generation.nkrCode }}</span
                >
                <span v-if="generation.parentImageId">
                  · {{ $t("message.image_generator.iteration_of") }} #{{
                    generation.parentImageId
                  }}
                </span>
                <span
                  v-if="generation.reviewAttempt > 1"
                  class="text-yellow-700"
                >
                  · {{ $t("message.image_generator.retry_of") }} #{{
                    generation.parentGenerationId
                  }}
                </span>
              </span>
              <span>{{ generation.createdAt }}</span>
            </div>
            <p class="text-sm mb-2">{{ generation.prompt }}</p>
            <p
              v-if="generation.parentPrompt"
              class="text-xs text-gray-400 italic mb-2 border-l-2 pl-2"
            >
              {{ $t("message.image_generator.from_prompt") }}
              {{ generation.parentPrompt }}
            </p>
            <p v-if="generation.error" class="text-xs text-red-600 mb-2">
              {{ generation.error }}
            </p>
            <p v-if="generation.reviewStatus" class="text-xs mb-2">
              <span
                :class="
                  generation.reviewStatus === 'approved'
                    ? 'text-green-600'
                    : 'text-yellow-600'
                "
              >
                <i
                  :class="
                    generation.reviewStatus === 'approved'
                      ? 'mdi mdi-check-circle'
                      : 'mdi mdi-alert-circle'
                  "
                ></i>
                {{
                  generation.reviewStatus === "approved"
                    ? $t("message.image_generator.review_approved")
                    : $t("message.image_generator.review_rejected")
                }}
                <span v-if="generation.reviewScore">
                  · {{ generation.reviewScore }}/10
                </span>
              </span>
              <span
                v-for="review in generation.reviews"
                :key="review.id"
                class="block text-gray-500"
              >
                {{ review.summary || review.error }}
                <ul
                  v-if="parseIssues(review).length"
                  class="list-disc list-inside"
                >
                  <li v-for="(issue, i) in parseIssues(review)" :key="i">
                    {{ issue }}
                  </li>
                </ul>
              </span>
            </p>
            <p class="text-xs text-gray-400 mb-2">
              {{ generation.aspectRatio }} · {{ generation.imageSize }} ·
              {{ generation.status }}
              <span v-if="generation.reviewAttempt > 1">
                · {{ $t("message.image_generator.attempt") }}
                {{ generation.reviewAttempt }}
              </span>
              <span v-if="generation.durationMs">
                · {{ Math.round(generation.durationMs / 1000) }}s
              </span>
            </p>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="image in generation.images"
                :key="image.id"
                class="relative"
              >
                <img
                  :src="image.url"
                  class="w-24 h-24 object-cover rounded border"
                />
                <button
                  class="absolute bottom-0 right-0 bg-white border rounded px-1 text-xs"
                  :title="$t('message.image_generator.iterate')"
                  @click="iterate(image)"
                >
                  <i class="mdi mdi-image-edit"></i>
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- Plantillas de prompt en tabla -->
        <AiPromptTemplatesTable
          v-if="tab === 'templates'"
          @apply="applyTemplateFromTable"
          @changed="templatesVersion++"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { generateImage, getImageGenerations } from "@/api/store/AiService.js";
import {
  simpleSearchProducts,
  getProduct,
  getProductImages
} from "@/api/store/ProductsService.js";
import AiStyleAssets from "@/components/ai/AiStyleAssets.vue";
import AiPromptTemplates from "@/components/ai/AiPromptTemplates.vue";
import AiPromptTemplatesTable from "@/components/ai/AiPromptTemplatesTable.vue";

export default {
  name: "ImageGeneratorPage",
  components: {
    AiStyleAssets,
    AiPromptTemplates,
    AiPromptTemplatesTable
  },
  data() {
    return {
      prompt: "",
      aspectRatio: "1:1",
      imageSize: "1K",
      count: 1,
      aspectRatios: [
        "1:1",
        "16:9",
        "9:16",
        "4:3",
        "3:4",
        "3:2",
        "2:3",
        "4:5",
        "5:4",
        "21:9"
      ],
      imageSizes: ["512", "1K", "2K", "4K"],
      // Producto elegido: su ficha viaja al backend y se adjunta al prompt.
      productSearch: "",
      productResults: [],
      searchingProducts: false,
      selectedProduct: null,
      productImages: [],
      selectedProductImages: [],
      // Referencias de la biblioteca: estilos elegidos + si van los por defecto
      // (el logo), que el backend adjunta solo.
      assetIds: [],
      includeDefaultAssets: true,
      promptTemplateId: null,
      // Revisión automática: Claude evalúa el resultado y reintenta con el
      // prompt corregido, sin que lo mire una persona. Va activada por defecto:
      // es el punto de la función, y apagarla es la excepción.
      autoReview: true,
      maxAttempts: 3,
      templatesVersion: 0,
      // Referencias subidas a mano (base64, sin el prefijo data:).
      uploadedReferences: [],
      dragging: false,
      // Imagen previa sobre la que se está iterando.
      iterationFrom: null,
      results: [],
      loading: false,
      tab: "results",
      generations: [],
      loadingHistory: false,
      historyOnlyMine: false,
      historyOnlyProduct: false
    };
  },
  // Se puede llegar con un producto ya elegido (?nkrCode=...), por ejemplo
  // desde el editor de listings de Amazon: evita volver a buscarlo a mano.
  mounted() {
    const nkrCode = this.$route.query.nkrCode;
    if (nkrCode) {
      this.selectProduct({ nkrCode });
    }
    // El prompt puede venir armado desde otra pantalla (p. ej. las imágenes que
    // Claude propone para un documento A+): se precarga para editarlo acá.
    if (this.$route.query.prompt) {
      this.prompt = this.$route.query.prompt;
    }
    // La relación de aspecto puede venir impuesta por el destino (los módulos
    // de A+ tienen medidas fijas); solo se acepta si es una de las válidas.
    const ratio = this.$route.query.aspectRatio;
    if (ratio && this.aspectRatios.includes(ratio)) {
      this.aspectRatio = ratio;
    }
  },
  methods: {
    // -- Plantillas -------------------------------------------------------
    applyTemplate(template) {
      this.promptTemplateId = template.id;
      this.prompt = template.prompt;
      if (template.aspectRatio) this.aspectRatio = template.aspectRatio;
      if (template.imageSize) this.imageSize = template.imageSize;
      if (template.count) this.count = template.count;
      if (template.assetIds && template.assetIds.length) {
        this.assetIds = template.assetIds;
      }
    },

    applyTemplateFromTable(template) {
      this.applyTemplate(template);
      this.tab = "results";
      this.templatesVersion++;
    },

    // -- Producto ---------------------------------------------------------
    onProductSearchInput() {
      clearTimeout(this._searchTimeout);
      if (this.productSearch.trim().length < 2) {
        this.productResults = [];
        return;
      }
      this._searchTimeout = setTimeout(() => this.searchProducts(), 300);
    },
    async searchProducts() {
      if (this._cancelSource) this._cancelSource.cancel();
      this._cancelSource = axios.CancelToken.source();
      this.searchingProducts = true;
      try {
        const res = await simpleSearchProducts(
          this.productSearch.trim(),
          20,
          this._cancelSource.token
        );
        this.productResults = res.products || [];
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.productResults = [];
        }
      } finally {
        this.searchingProducts = false;
      }
    },
    async selectProduct(product) {
      this.productResults = [];
      this.productSearch = "";
      try {
        const [detail, images] = await Promise.all([
          getProduct(product.nkrCode),
          getProductImages(product.nkrCode)
        ]);
        this.selectedProduct = detail.part;
        this.productImages = images || [];
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.product_error"));
      }
    },
    clearProduct() {
      this.selectedProduct = null;
      this.productImages = [];
      this.selectedProductImages = [];
      this.historyOnlyProduct = false;
    },
    isProductImageSelected(image) {
      return this.selectedProductImages.includes(image.imageRoute);
    },
    toggleProductImage(image) {
      const index = this.selectedProductImages.indexOf(image.imageRoute);
      if (index === -1) {
        this.selectedProductImages.push(image.imageRoute);
      } else {
        this.selectedProductImages.splice(index, 1);
      }
    },

    // -- Referencias subidas ----------------------------------------------
    onDrop(event) {
      this.dragging = false;
      this.addFiles(Array.from(event.dataTransfer.files || []));
    },
    onReferenceFiles(event) {
      this.addFiles(Array.from(event.target.files || []));
      event.target.value = "";
    },
    addFiles(files) {
      files
        .filter(file => file.type.startsWith("image/"))
        .forEach(file => {
          const reader = new FileReader();
          reader.onload = () => {
            // El backend espera base64 puro, sin el prefijo "data:<mime>;base64,".
            const base64 = String(reader.result).split(",")[1];
            this.uploadedReferences.push({
              mimeType: file.type,
              data: base64,
              name: file.name
            });
          };
          reader.readAsDataURL(file);
        });
    },
    removeReference(index) {
      this.uploadedReferences.splice(index, 1);
    },

    // -- Generación --------------------------------------------------------
    iterate(image) {
      this.iterationFrom = image;
      // El prompt del padre lo recupera el backend a partir de parentImageId,
      // así que aquí se limpia para que se escriban sólo los cambios nuevos.
      this.prompt = "";
      this.tab = "results";
      // En escritorio el formulario tiene su propio scroll, así que subir la
      // página no alcanza para mostrar el aviso de iteración.
      const column = this.$refs.configColumn;
      if (column && column.scrollTop) {
        column.scrollTo({ top: 0, behavior: "smooth" });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async generate() {
      if (!this.prompt.trim() || this.loading) return;
      this.loading = true;
      const prompt = this.prompt.trim();
      try {
        const data = await generateImage({
          prompt,
          nkrCode: this.selectedProduct ? this.selectedProduct.nkrCode : null,
          aspectRatio: this.aspectRatio,
          imageSize: this.imageSize,
          count: this.count,
          images: this.uploadedReferences,
          referenceUrls: this.selectedProductImages,
          assetIds: this.assetIds,
          includeDefaultAssets: this.includeDefaultAssets,
          promptTemplateId: this.promptTemplateId,
          autoReview: this.autoReview,
          maxAttempts: this.maxAttempts,
          parentImageId: this.iterationFrom ? this.iterationFrom.id : null
        });
        const raw = data.attempts || [];
        const attempts = raw.map((attempt, index) => ({
          ...attempt,
          // Cada reintento corrió con el prompt corregido que propuso la
          // revisión del intento anterior, no con el original.
          prompt:
            index > 0 && raw[index - 1].review
              ? raw[index - 1].review.improvedPrompt || prompt
              : prompt,
          nkrCode: this.selectedProduct ? this.selectedProduct.nkrCode : null
        }));
        // Los más recientes primero, conservando el historial de la sesión.
        this.results = [...attempts.reverse(), ...this.results];
        this.iterationFrom = null;
        if (this.autoReview && !data.approved) {
          this.$toast.warning(
            this.$t("message.image_generator.review_not_approved")
          );
        }
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.error || this.$t("message.image_generator.error")
        );
      } finally {
        this.loading = false;
      }
    },
    // issues se guarda como JSON en una columna de texto.
    parseIssues(review) {
      try {
        return review.issues ? JSON.parse(review.issues) : [];
      } catch (e) {
        return [];
      }
    },
    copyUrl(url) {
      navigator.clipboard.writeText(url);
      this.$toast.success(this.$t("message.image_generator.url_copied"));
    },

    // -- Historial ---------------------------------------------------------
    openHistory() {
      this.tab = "history";
      if (!this.generations.length) {
        this.loadHistory();
      }
    },
    async loadHistory() {
      this.loadingHistory = true;
      try {
        const data = await getImageGenerations({
          mine: this.historyOnlyMine ? 1 : null,
          nkrCode:
            this.historyOnlyProduct && this.selectedProduct
              ? this.selectedProduct.nkrCode
              : null,
          limit: 30
        });
        this.generations = data.generations || [];
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.history_error"));
      } finally {
        this.loadingHistory = false;
      }
    }
  }
};
</script>

<style scoped>
/* En escritorio cada columna scrollea por separado: el formulario es largo y
   los resultados crecen sin parar, y con un único scroll de página se perdía
   de vista el prompt al mirar una imagen.
   65px del header + el margen y el título de la página. */
@media (min-width: 1024px) {
  .column-scroll {
    max-height: calc(100vh - 12rem);
    overflow-y: auto;
    /* Aire para que la barra de scroll no quede encima del contenido ni tape
       el borde de los inputs al enfocarlos, y para que el último bloque no
       termine pegado al fondo. */
    padding-right: 1.25rem;
    padding-bottom: 1.5rem;
  }

  /* Barra fina y discreta, para que ocupe poco de ese margen. */
  .column-scroll::-webkit-scrollbar {
    width: 8px;
  }
  .column-scroll::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 4px;
  }
  .column-scroll::-webkit-scrollbar-thumb:hover {
    background-color: #9ca3af;
  }
  .column-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
}
</style>
